import { DAY_MS } from "@ui/i18n/formatDate";
import React from "react";
import { useBreaksBetweenGroups } from "../../Analysis/useBreaksBetweenGroups";

export interface GroupBreak {
  from: number;
  to: number;
}

export function computeGroupBreaks(
  groupables: {
    date: number;
    group: string | null;
  }[],
): GroupBreak[] {
  if (groupables.length < 2) {
    return [];
  }

  const { breaks } = groupables.slice(1).reduce<{
    breaks: GroupBreak[];
    lastGroupable: {
      date: number;
      group: string | null;
    };
  }>(
    (acc, groupable) => {
      if (groupable.group !== acc.lastGroupable.group) {
        acc.breaks.push({
          from: acc.lastGroupable.date,
          to: groupable.date,
        });
      }
      acc.lastGroupable = groupable;
      return acc;
    },
    {
      breaks: [],
      lastGroupable: groupables[0]!,
    },
  );

  return breaks;
}

export default function useGroupBreaks(
  projectId: string,
  groupables: {
    date: number;
    group: string | null;
  }[],
) {
  const [breakBetweenGroups] = useBreaksBetweenGroups({
    scope: projectId,
  });
  return React.useMemo(() => {
    if (!breakBetweenGroups) return [];
    const groupBreaks = computeGroupBreaks(groupables);

    return groupBreaks.map(groupBreak => {
      const length = groupBreak.to - groupBreak.from;

      return {
        from: groupBreak.from + Math.min(DAY_MS, length / 10),
        to: groupBreak.to - Math.min(DAY_MS, length / 10),
      };
    });
  }, [groupables, breakBetweenGroups]);
}
